<script lang="ts" setup>
import { i18n } from '@/i18n';
import { computed, ref } from 'vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import SelectInput from '@/corePagesModule/SelectInput';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { FilterType } from '@/coreDomainsModule/entities/filter';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';

// props
const props = withDefaults(
  defineProps<{
    modalState: boolean;
    selectInputIndex?: number; // SelectInputの排他制御で、現在の画面で使ってるSelectInputのindexが被ってはいけないので、indexを渡す
  }>(),
  {
    selectInputIndex: 1,
  }
);

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'apply', selectedResponsibleUserId: string, receivedYearMonth: IYearMonth): void;
}>();

//変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

const currentFilterType = ref<FilterType | undefined>(undefined);
const startYearMonth = ref<IYearMonth>(DateService.getShiftedIYearMonthFromNowDateByMonths(0));
const selectedResponsibleUserId = ref('');
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :back-button-label="t('もどる')"
    :next-button-label="t('ダウンロード')"
    :modal-width="500"
    :is-disabled-next-button="!selectedResponsibleUserId || !startYearMonth.year || !startYearMonth.month"
    :is-disabled-scroll="true"
    @on-back="emits('close')"
    @on-next="emits('apply', selectedResponsibleUserId, startYearMonth)"
  >
    <template #title>{{ t('受領用内示ダウンロード') }}</template>
    <div class="download-forecast-modal__main-content main-content">
      <div class="main-content__select-responsible-user select-responsible-user">
        <div class="select-responsible-user__label">
          {{ t('ダウンロードする受領用内示の担当者') }}
        </div>
        <SelectInput
          class="main-content__select-input"
          :index="selectInputIndex"
          :model-value="selectedResponsibleUserId"
          :placeholder="t('担当者')"
          :label="t('')"
          :filter-type="'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER'"
          :is-open-prop="currentFilterType === 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER'"
          @click:select-input="() => (currentFilterType = 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER')"
          @update:model-value="selectedResponsibleUserId = $event"
        />
      </div>
      <div class="main-content__select-period select-period">
        <div class="select-period__label">
          {{ t('開始月') }}
        </div>
        <div class="select-period__content-container">
          <div class="select-period__content">
            <div class="select-period__picker-container">
              <MonthPicker
                class="select-period__calendar-picker"
                :model-value="startYearMonth"
                :prop-format="t('MonthPickerFormat')"
                :placeholder="'2024/10'"
                :locale="locale"
                :use-teleport="false"
                @update:model-value="startYearMonth = $event"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.main-content {
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 24px;
  align-content: space-between;
}

.select-responsible-user {
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 16px;
  background-color: $backgroundDefault;
  padding: 16px;
  border-radius: 8px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__content-container {
    display: grid;
    grid-template-rows: repeat(2, auto);
    gap: 8px;
    padding-left: 16px;
  }

  &__select-file-button {
    width: 160px;
    height: 40px;
    margin-left: 16px;
  }

  &__file-input {
    display: none;
  }
}

.select-period {
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 16px;
  background-color: $backgroundDefault;
  padding: 16px;
  border-radius: 8px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__content-container {
    display: grid;
    grid-template-rows: auto;
    gap: 8px;
  }

  &__content {
    display: grid;
  }

  &__attention {
    font-size: 16px;
    font-weight: 500;
    color: $semanticError;
  }

  &__picker-container {
    display: grid;
    align-items: center;
  }

  &__calendar-picker {
    width: 100px;
    height: 32px;
    border-bottom: 1px solid $borderDefault;
    display: grid;
    place-items: center;
    background-color: $semanticWhite;
  }
}
</style>

<style lang="scss">
.select-period__calendar-picker input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
}
</style>
